import i18n from "i18next";
import { CustomerOfferPartCostOverrideApi } from "src/api/generated/erp/sales/customerOrderOffer/api/customerOfferPartCostOverrideApi.ts";
import { CustomerOrderPartCostOverrideApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderPartCostOverrideApi.ts";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { PartCostOverrideConditions } from "src/api/generated/io/aavo/applications/db/erp/types/partCostOverrideConditions.ts";
import { PartCostOverrideOptionsProvider_PropertyValueOption } from "src/api/generated/erp/sales/partCostOverride/partCostOverrideOptionsProvider.ts";

/** Customer offer or order whose part cost overrides are managed. */
export type PartCostOverrideOwner =
	{ type: "customerOffer"; customerOrderOfferId: number } | { type: "customerOrder"; customerOrderId: number };

export interface PartCostOverrideRow {
	partCostOverrideId: number;
	partId: number;
	partNo: string;
	partDescription_1: string;
	propertyConditions: PartCostOverrideConditions;
	unitCost: number;
}

export interface PartCostOverrideValues {
	partId: number;
	propertyConditions: PartCostOverrideConditions;
	unitCost: number;
}

export interface PartCostOverrideOwnerApi {
	getPartCostOverrides: () => Promise<PartCostOverrideRow[]>;
	getPartOptions: () => Promise<PartView[]>;
	getPropertyValueOptions: (partId: number) => Promise<PartCostOverrideOptionsProvider_PropertyValueOption[]>;
	createPartCostOverride: (values: PartCostOverrideValues) => Promise<void>;
	updatePartCostOverride: (partCostOverrideId: number, values: PartCostOverrideValues) => Promise<void>;
	deletePartCostOverride: (partCostOverrideId: number) => Promise<void>;
}

export function getPartCostOverrideOwnerApi(owner: PartCostOverrideOwner): PartCostOverrideOwnerApi {
	switch (owner.type) {
		case "customerOffer": {
			const customerOrderOfferId = owner.customerOrderOfferId;
			return {
				getPartCostOverrides: async () =>
					(await CustomerOfferPartCostOverrideApi.getPartCostOverrides({ customerOrderOfferId })).map(
						(row) => ({ ...row, partCostOverrideId: row.customerOfferPartCostId }),
					),
				getPartOptions: () => CustomerOfferPartCostOverrideApi.getPartOptions({ customerOrderOfferId }),
				getPropertyValueOptions: (partId) =>
					CustomerOfferPartCostOverrideApi.getPropertyValueOptions({ customerOrderOfferId, partId }),
				createPartCostOverride: (values) =>
					CustomerOfferPartCostOverrideApi.createPartCostOverride({ customerOrderOfferId, ...values }),
				updatePartCostOverride: (partCostOverrideId, values) =>
					CustomerOfferPartCostOverrideApi.updatePartCostOverride({
						customerOrderOfferId,
						customerOfferPartCostId: partCostOverrideId,
						...values,
					}),
				deletePartCostOverride: (partCostOverrideId) =>
					CustomerOfferPartCostOverrideApi.deletePartCostOverride({
						customerOrderOfferId,
						customerOfferPartCostId: partCostOverrideId,
					}),
			};
		}
		case "customerOrder": {
			const customerOrderId = owner.customerOrderId;
			return {
				getPartCostOverrides: async () =>
					(await CustomerOrderPartCostOverrideApi.getPartCostOverrides({ customerOrderId })).map((row) => ({
						...row,
						partCostOverrideId: row.customerOrderPartCostId,
					})),
				getPartOptions: () => CustomerOrderPartCostOverrideApi.getPartOptions({ customerOrderId }),
				getPropertyValueOptions: (partId) =>
					CustomerOrderPartCostOverrideApi.getPropertyValueOptions({ customerOrderId, partId }),
				createPartCostOverride: (values) =>
					CustomerOrderPartCostOverrideApi.createPartCostOverride({ customerOrderId, ...values }),
				updatePartCostOverride: (partCostOverrideId, values) =>
					CustomerOrderPartCostOverrideApi.updatePartCostOverride({
						customerOrderId,
						customerOrderPartCostId: partCostOverrideId,
						...values,
					}),
				deletePartCostOverride: (partCostOverrideId) =>
					CustomerOrderPartCostOverrideApi.deletePartCostOverride({
						customerOrderId,
						customerOrderPartCostId: partCostOverrideId,
					}),
			};
		}
	}
}

export function formatPartCostOverrideConditions(propertyConditions: PartCostOverrideConditions): string {
	if (propertyConditions.conditions.length === 0) return i18n.t("part_cost_override_no_conditions");
	return propertyConditions.conditions
		.map((condition) => `${condition.propertyName} = ${condition.propertyValue}`)
		.join(", ");
}
